import { useMemo, useRef, useState, useEffect, type FormEvent } from 'react';
import {
  addDoc,
  collection,
  limit,
  orderBy,
  query,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useCollection } from '@/hooks/firestore';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { toast } from '@/components/ui/Toast';
import { cn } from '@/lib/cn';
import type { BookingMessageDoc } from '@makhe/shared';

/**
 * In-app chat for a booking — keeps coordination on-platform (no phone-number
 * sharing). Only mounted once a booking is accepted and only for the two
 * participants (enforced by Firestore rules).
 */
export function BookingMessages({
  bookingId,
  currentUid,
  counterpartyName,
}: {
  bookingId: string;
  currentUid: string;
  counterpartyName: string;
}) {
  const q = useMemo(
    () =>
      query(
        collection(db, 'bookings', bookingId, 'messages'),
        orderBy('createdAt', 'asc'),
        limit(200),
      ),
    [bookingId],
  );
  const { data: messages, loading, error } = useCollection<BookingMessageDoc>(q);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: 'nearest' });
  }, [messages.length]);

  const send = async (e: FormEvent) => {
    e.preventDefault();
    const body = text.trim();
    if (!body) return;
    setSending(true);
    try {
      await addDoc(collection(db, 'bookings', bookingId, 'messages'), {
        senderId: currentUid,
        text: body.slice(0, 2000),
        createdAt: serverTimestamp(),
      });
      setText('');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not send message.');
    } finally {
      setSending(false);
    }
  };

  return (
    <Card className="mt-4">
      <p className="font-medium">Messages</p>
      <p className="mt-1 text-xs text-makhe-muted">
        Chat with {counterpartyName} here — everything stays inside Makhe.
      </p>

      <div className="mt-3 max-h-72 space-y-2 overflow-y-auto" aria-live="polite">
        {loading && <p className="text-xs text-makhe-muted">Loading…</p>}
        {!loading && error && (
          <p className="text-xs text-makhe-plum">Couldn’t load messages. Try again in a moment.</p>
        )}
        {!loading && !error && messages.length === 0 && (
          <p className="text-xs text-makhe-muted">
            No messages yet. Say hi and sort out the details.
          </p>
        )}
        {messages.map((m) => {
          const mine = m.senderId === currentUid;
          return (
            <div key={m.id} className={cn('flex', mine ? 'justify-end' : 'justify-start')}>
              <span
                className={cn(
                  'inline-block max-w-[80%] whitespace-pre-wrap break-words rounded-2xl px-3 py-2 text-sm',
                  mine
                    ? 'rounded-br-sm bg-makhe-primary text-makhe-on'
                    : 'rounded-bl-sm bg-makhe-surface text-makhe-ink',
                )}
              >
                {m.text}
              </span>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>

      <form onSubmit={send} className="mt-3 flex items-center gap-2">
        <label className="flex-1">
          <span className="sr-only">Message {counterpartyName}</span>
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={`Message ${counterpartyName}…`}
            maxLength={2000}
            className="w-full rounded-full border border-makhe-line bg-makhe-paper/60 px-4 py-2 text-sm text-makhe-ink placeholder:text-makhe-muted/60 focus:border-makhe-ink focus:outline-none"
          />
        </label>
        <Button type="submit" size="sm" loading={sending} disabled={!text.trim()}>
          Send
        </Button>
      </form>
    </Card>
  );
}
